import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { useDebounce } from '../../../hooks/useDebounce'
import { api } from '../../../api/axios'

// Provas Pendentes: por atirador, as modalidades que ainda faltam (inscricao sem resultado lancado)
// e quantas, com a etapa; e o resumo por modalidade (rota /inscricoes/pendentes da API). Canceladas nao entram.

interface Pendentes {
  resumo: { atiradores: number; modalidades: number; falta: number }
  modalidades: Array<{ idmodalidade: number; nome: string; grupo: string | null; atiradores: number; falta: number }>
  atiradores: Array<{
    idatirador: number; nome: string; cpf: string | null; falta: number
    modalidades: Array<{ idmodalidade: number; nome: string; falta: number; etapas: Array<{ numero_etapa: number; falta: number }> }>
  }>
}

const esc = (s: unknown) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string))
const etapasTexto = (etapas: Array<{ numero_etapa: number; falta: number }>) =>
  etapas.length === 1 ? `${etapas[0].numero_etapa}ª` : etapas.map(e => `${e.numero_etapa}ª (${e.falta})`).join(', ')

export function ProvasPendentesPage() {
  const [idcampeonato, setIdcampeonato] = useState<number | ''>('')
  const [numeroEtapa, setNumeroEtapa] = useState<number | ''>('')
  const [idmodalidade, setIdmodalidade] = useState<number | ''>('')
  const [atirador, setAtirador] = useState<{ idatirador: number; nome: string } | null>(null)
  const [busca, setBusca] = useState('')
  const [sugestoes, setSugestoes] = useState(false)

  const { data: campeonatosData } = useCampeonatos({ limit: 100 })
  const { data: modalidadesData } = useModalidades(idcampeonato ? { idcampeonato, limit: 100 } : { limit: 100 })
  const buscaDebounced = useDebounce(busca, 400)
  const { data: atiradoresData, isLoading: buscando } = useAtiradores(buscaDebounced.length >= 3 ? { nome: buscaDebounced, limit: 10 } : {})
  const atiradoresBusca = buscaDebounced.length >= 3 ? atiradoresData?.data ?? [] : []

  const filtros = {
    ...(idcampeonato ? { idcampeonato } : {}),
    ...(numeroEtapa ? { numero_etapa: numeroEtapa } : {}),
    ...(idmodalidade ? { idmodalidade } : {}),
    ...(atirador ? { idatirador: atirador.idatirador } : {}),
  }
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inscricoes-pendentes', filtros],
    queryFn: async () => (await api.get<{ data: Pendentes }>('/inscricoes/pendentes', { params: filtros })).data.data,
  })

  const campeonatoNome = campeonatosData?.data?.find(c => Number(c.idcampeonato) === Number(idcampeonato))?.descricao
  const modalidadeNome = modalidadesData?.data?.find(m => Number(m.idmodalidade) === Number(idmodalidade))?.nome

  function limpar() {
    setIdcampeonato(''); setNumeroEtapa(''); setIdmodalidade(''); setAtirador(null); setBusca('')
  }

  function imprimir() {
    if (!data) return
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Provas Pendentes</title><style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:16px} h1{font-size:16px;margin:0 0 4px} h2{font-size:13px;margin:16px 0 4px}
      table{border-collapse:collapse;width:100%} th,td{border:1px solid #999;padding:3px 5px;text-align:left;vertical-align:top} th{background:#eee}
      .n{text-align:right} .sub{color:#555;font-size:11px} tfoot td,.tot td{font-weight:bold;background:#f5f5f5} tbody{page-break-inside:auto} tr{page-break-inside:avoid}</style></head><body>
      <h1>Provas Pendentes</h1>
      <div class="sub">${esc(campeonatoNome ?? 'Todos os campeonatos')} - ${numeroEtapa ? `${numeroEtapa}ª etapa` : 'todas as etapas'} - ${esc(modalidadeNome ?? 'todas as modalidades')} - ${esc(atirador?.nome ?? 'todos os atiradores')}</div>
      <h2>Por atirador</h2><table><thead><tr><th>Atirador</th><th>Modalidade que falta</th><th>Etapa</th><th class="n">Falta</th></tr></thead><tbody>
      ${data.atiradores.map(a => a.modalidades.map((m, i) => `<tr>${i === 0 ? `<td rowspan="${a.modalidades.length + 1}"><b>${esc(a.nome)}</b></td>` : ''}<td>${esc(m.nome)}</td><td>${etapasTexto(m.etapas)}</td><td class="n">${m.falta}</td></tr>`).join('')
        + `<tr class="tot"><td colspan="2">Total do atirador</td><td class="n">${a.falta}</td></tr>`).join('')}
      </tbody><tfoot><tr><td colspan="3">TOTAL (${data.resumo.atiradores} atiradores)</td><td class="n">${data.resumo.falta}</td></tr></tfoot></table>
      <h2>Por modalidade</h2><table><thead><tr><th>Modalidade</th><th class="n">Atiradores</th><th class="n">Falta</th></tr></thead><tbody>
      ${data.modalidades.map(m => `<tr><td>${esc(m.nome)}</td><td class="n">${m.atiradores}</td><td class="n">${m.falta}</td></tr>`).join('')}</tbody>
      <tfoot><tr><td>TOTAL</td><td class="n">${data.resumo.atiradores}</td><td class="n">${data.resumo.falta}</td></tr></tfoot></table>
      <script>window.onload=function(){window.print()}</script></body></html>`
    const w = window.open('', '_blank')
    if (!w) return
    w.document.write(html); w.document.close()
  }

  const cartao = (titulo: string, valor: string | number, destaque = false) => (
    <div className={`rounded-xl border p-4 ${destaque ? 'border-compumaq-dourado bg-compumaq-dourado/10' : 'border-compumaq-dourado/25 bg-compumaq-pretoSuave'}`}>
      <div className="text-xs text-compumaq-textoSuave">{titulo}</div>
      <div className="text-2xl font-bold text-compumaq-texto">{valor}</div>
    </div>
  )
  const th = 'px-3 py-2 text-left text-xs font-semibold text-compumaq-textoSuave uppercase'
  const td = 'px-3 py-2 text-sm'
  const tdn = 'px-3 py-2 text-sm text-right tabular-nums'

  return (
    <div className="space-y-4">
      <PageHeader
        title="Provas Pendentes"
        description="Atiradores e as modalidades que ainda faltam atirar (inscricao sem resultado lancado)"
        action={
          <button onClick={imprimir} disabled={!data} className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-40">
            Imprimir
          </button>
        }
      />

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select aria-label="Campeonato" className={selectClass} value={idcampeonato} onChange={e => { setIdcampeonato(e.target.value ? parseInt(e.target.value) : ''); setIdmodalidade('') }}>
              <option value="">Todos</option>
              {campeonatosData?.data?.map(c => <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao}</option>)}
            </select>
          </div>
          <div className="w-40">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
            <select aria-label="Etapa" className={selectClass} value={numeroEtapa} onChange={e => setNumeroEtapa(e.target.value ? parseInt(e.target.value) : '')}>
              <option value="">Todas as etapas</option>
              {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n}ª Etapa</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Modalidade</label>
            <select aria-label="Modalidade" className={selectClass} value={idmodalidade} onChange={e => setIdmodalidade(e.target.value ? parseInt(e.target.value) : '')}>
              <option value="">Todas</option>
              {(modalidadesData?.data ?? []).slice().sort((a, b) => a.nome.localeCompare(b.nome)).map(m => <option key={m.idmodalidade} value={m.idmodalidade}>{m.nome}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[240px] relative">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
            {atirador ? (
              <div className="flex items-center gap-2">
                <div data-atirador-escolhido className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">{atirador.nome}</div>
                <button onClick={() => { setAtirador(null); setBusca('') }} className="px-3 py-2 text-sm text-red-600 hover:text-red-800 border border-compumaq-dourado/25 rounded-lg hover:bg-compumaq-preto">Todos</button>
              </div>
            ) : (
              <div className="relative">
                <input type="text" placeholder="Todos (digite para escolher um)" className={inputClass} value={busca}
                  onChange={e => { setBusca(e.target.value); setSugestoes(true) }} onFocus={() => setSugestoes(true)} />
                {buscando && buscaDebounced.length >= 3 && <div className="absolute right-3 top-2.5"><Spinner size="sm" /></div>}
                {sugestoes && atiradoresBusca.length > 0 && (
                  <div className="absolute z-10 w-full bg-compumaq-pretoSuave border border-compumaq-dourado/25 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {atiradoresBusca.map(a => (
                      <button key={a.idatirador} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-compumaq-preto border-b border-compumaq-dourado/25 last:border-0"
                        onClick={() => { setAtirador({ idatirador: Number(a.idatirador), nome: a.nome }); setBusca(a.nome); setSugestoes(false) }}>
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-compumaq-textoSuave text-xs ml-2">{a.cpf}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <button onClick={limpar} className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto">Limpar</button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : isError || !data ? (
        <div className="text-sm text-red-600">Nao foi possivel carregar as provas pendentes.</div>
      ) : (
        <>
          <div data-resumo className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {cartao('Atiradores com prova pendente', data.resumo.atiradores)}
            {cartao('Modalidades com prova pendente', data.resumo.modalidades)}
            {cartao('Provas que faltam', data.resumo.falta, true)}
          </div>

          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-x-auto">
            <h2 className="px-4 pt-3 font-semibold text-compumaq-texto">Por atirador ({data.atiradores.length})</h2>
            <table data-por-atirador className="w-full mt-2">
              <thead className="border-b border-compumaq-dourado/25">
                <tr><th className={th}>Atirador</th><th className={th}>Modalidade que falta</th><th className={th}>Etapa</th><th className={`${th} text-right`}>Falta</th></tr>
              </thead>
              <tbody>
                {data.atiradores.length === 0 && <tr><td colSpan={4} className={`${td} text-compumaq-textoSuave`}>Nenhuma prova pendente com esses filtros.</td></tr>}
                {data.atiradores.map(a => [
                  ...a.modalidades.map((m, i) => (
                    <tr key={`${a.idatirador}-${m.idmodalidade}`} data-atirador={a.idatirador} className="border-b border-compumaq-dourado/10 align-top">
                      {i === 0 && <td rowSpan={a.modalidades.length + 1} className={`${td} font-medium`}>{a.nome}</td>}
                      <td className={td}>{m.nome}</td>
                      <td className={`${td} text-xs`}>{etapasTexto(m.etapas)}</td>
                      <td className={tdn}>{m.falta}</td>
                    </tr>
                  )),
                  <tr key={`${a.idatirador}-total`} data-total-atirador={a.idatirador} className="border-b border-compumaq-dourado/25 bg-compumaq-dourado/5">
                    <td colSpan={2} className={`${td} text-xs text-compumaq-textoSuave`}>Total do atirador</td>
                    <td className={`${tdn} font-semibold`}>{a.falta}</td>
                  </tr>,
                ])}
              </tbody>
              <tfoot>
                <tr className="bg-compumaq-dourado/10 font-bold">
                  <td colSpan={3} className={td}>TOTAL ({data.resumo.atiradores} atiradores)</td><td className={tdn}>{data.resumo.falta}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-x-auto">
            <h2 className="px-4 pt-3 font-semibold text-compumaq-texto">Por modalidade</h2>
            <table data-por-modalidade className="w-full mt-2">
              <thead className="border-b border-compumaq-dourado/25">
                <tr><th className={th}>Modalidade</th><th className={`${th} text-right`}>Atiradores</th><th className={`${th} text-right`}>Falta</th></tr>
              </thead>
              <tbody>
                {data.modalidades.map(m => (
                  <tr key={m.idmodalidade} className="border-b border-compumaq-dourado/10">
                    <td className={td}>{m.nome}</td><td className={tdn}>{m.atiradores}</td><td className={`${tdn} font-semibold`}>{m.falta}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-compumaq-dourado/10 font-bold">
                  <td className={td}>TOTAL</td><td className={tdn}>{data.resumo.atiradores}</td><td className={tdn}>{data.resumo.falta}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
